import { fieldTypesAgGrid, filterTypesAgGrid } from '../../constants/globals';
import store from '../../reducers/index';
import { sortData } from '../../utils';
import { ActionTypes } from '../Administration/utils';
import { filterAllowedActions } from './action-utils';
import ActionSuppComp from './Components/ActionSuppComp';
import ActiveFilter from './Components/ActiveFilter';
import ActiveSuppComp from './Components/ActiveSuppComp';

export const billModelConstants = {
  BILL_USERS: 'billUsers',
  BILL_BUSINESS_PARTNERS: 'billBusinessPartners',
  BILL_CHANNELS: 'billChannels',
  BILL_BROADCASTER_CHANNELS: 'billBroadcasterChannels',
};

export const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      minWidth: 225,
      maxWidth: 225,
      width: 225,
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
      minWidth: 180,
      maxWidth: 400,
      width: 250,
    },
  ],
  position: 'right',
  defaultToolPanel: '',
};

export const getFilterType = (type) => {
  switch (type) {
    case filterTypesAgGrid.TEXT:
      return 'agTextColumnFilter';
    case filterTypesAgGrid.DATE:
      return 'agDateColumnFilter';
    case filterTypesAgGrid.SET:
      return 'agSetColumnFilter';
    case filterTypesAgGrid.BOOLEAN:
      return ActiveFilter;
    default:
      return 'agTextColumnFilter';
  }
};

export const getFilterParams = (type, translation) => {
  switch (type) {
    case filterTypesAgGrid.DATE:
      return filterParamsForDate;
    case filterTypesAgGrid.BOOLEAN:
      return {
        ...translation,
        values: ['☑', '☐'],
        debounceMs: 0,
        buttons: ['clear'],
      };
    default:
      return {
        buttons: ['clear'],
      };
  }
};

export const getComparator = (type) => {
  if (type === filterTypesAgGrid.DATE) return dateComparator;
  return undefined;
};

export const getCellRenderParams = (item, translations, action) => {
  switch (item?.type) {
    case fieldTypesAgGrid.BOOLEAN:
      return { translation: translations?.Boolean };
    case fieldTypesAgGrid.ACTION:
      const { setAction, allowedActions } = action || {};
      const actionsSettingsPerRow =
        item?.actionsSettings?.filter(
          (a) =>
            a?.type !== ActionTypes.CREATE &&
            a?.type !== ActionTypes.VERIFY &&
            a?.type !== ActionTypes.CONSENT_GIVE
        ) || [];
      return {
        actionSettings: filterAllowedActions(
          actionsSettingsPerRow,
          allowedActions
        ),
        onClick: setAction,
      };
    default:
      return {};
  }
};

export const getCellRenderer = (type) => {
  switch (type) {
    case fieldTypesAgGrid.BOOLEAN:
      return ActiveSuppComp;
    case fieldTypesAgGrid.ACTION:
      return ActionSuppComp;
    default:
      return undefined;
  }
};


const checkBoxColumn = {
  headerName: '',
  checkboxSelection: true,
  headerCheckboxSelection: true,
  width: 50,
  pinned: 'left',
  lockPosition: 'left',
  suppressMovable: true,
  lockVisible: true,
};

export const mapperSettings = (settings, translation, action) => {
  const { ActiveFilterTranslation, CellRender } = translation || {},
    { hideActionColumn } = action || {};
  const tableSettings = hideActionColumn
    ? settings?.filter((s) => s.type !== fieldTypesAgGrid.ACTION)
    : settings;

  return [checkBoxColumn].concat(
    tableSettings?.map((item) => ({
      ...item,
      headerName: item.label,
      filter: getFilterType(item?.typeFilter),
      filterParams: getFilterParams(item?.typeFilter, ActiveFilterTranslation),
      cellRenderer: getCellRenderer(item?.type),
      cellRendererParams: getCellRenderParams(item, CellRender, action),
      tooltipField: item?.tooltipField ? `${item.field}` : undefined,
      flex: item?.autoSize ? 1 : undefined,
      headerClass: item?.resizable === false ? 'fixed-size-header' : '',
      comparator: getComparator(item?.typeFilter),
      hide: !!item?.hide,
    }))
  );
};

export function prepareAggridSettings(data, withoutActionColumn) {
  let filteredData = data?.filter((d) => d.enabled);
  if (withoutActionColumn) {
    filteredData = filteredData?.map((d) => {
      if (d?.type !== fieldTypesAgGrid.ACTION) return d;
      return {
        ...d,
        actionsSettings: d.actionsSettings?.filter(
          (a) => a.type === ActionTypes.CREATE || a.type === ActionTypes.PREVIEW
        ),
        width: 80,
      };
    });
  }
  return sortData(filteredData, 'orderNum');
}

const unauthenticatedUserDate = new Date(
  'Thu Jan 01 1970 01:00:00 GMT+0100'
).getTime();



function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

export const filterParamsForDate = {
  filterOptions: [
    'equals',
    'lessThan',
    'greaterThan',
    'lessThanOrEqual',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ],
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    let splitSeparator = '.';
    const splitetCellValue = cellValue.split(splitSeparator);

    if (!splitetCellValue || '') return '';
    const startOfCellDate = formDateFromDatePartsForFilter(splitetCellValue);

    if (filterLocalDateAtMidnight.getTime() === startOfCellDate.getTime()) {
      return 0;
    }
    if (startOfCellDate.getTime() < filterLocalDateAtMidnight.getTime()) {
      return -1;
    }
    if (startOfCellDate.getTime() > filterLocalDateAtMidnight.getTime()) {
      return 1;
    }
  },
  browserDatePicker: true,
  minValidYear: 2016,
  inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
  buttons: ['clear'],
};

function prepareDateObject(date) {
  const splitetCellValue = date.split('.');
  return formDateFromDateParts(splitetCellValue);
}

export function dateComparator(date1, date2) {
  const date1Number = date1 && prepareDateObject(date1).getTime();
  const date2Number = date2 && prepareDateObject(date2).getTime();

  if (date1Number == null && date2Number == null) {
    return 0;
  }

  if (date1Number == null) {
    return -1;
  } else if (date2Number == null) {
    return 1;
  }

  return date1Number - date2Number;
}

function formDateFromDateParts(dateParts) {
  const cellDate = new Date();
  cellDate.setFullYear(dateParts[2]);
  cellDate.setDate(dateParts[0]);
  cellDate.setMonth(dateParts[1] - 1);
  cellDate.setUTCHours(0, 0, 0, 0);
  const startOfCellDate = new Date(cellDate.getTime() - 2 * 1000 * 3600);
  return startOfCellDate;
}

function formDateFromDatePartsForFilter(dateParts) {
  let date = `${dateParts[1]}/${dateParts[0]}/${dateParts[2]}`;
  const startOfCellDate = new Date(Date.parse(date));
  return startOfCellDate;
}

export const clearFiltersForAllColumns = (gridRef) => {
  gridRef?.current?.api?.setFilterModel?.(null);
  gridRef?.current?.api?.filterManager?.activeColumnFilters
    ?.filter((a) => a?.getModel() === filterTypesAgGrid.BOOLEAN)
    ?.forEach((b) => b?.componentInstance.resetFilter());
};

export function prepareResponseData(data, table, inForm) {
  return { data, inForm: inForm ? table : null };
}

export const getSettingsAndDataForForm = (type) => {
  const { agGridReducer, attributeReducer } = store?.getState() || {};
  return {
    rowsList: agGridReducer?.[`${type}Rows`] || [],
    columnSettingsList: agGridReducer?.[`${type}Columns`] || [],
    filterSettings: attributeReducer?.[`${type}Filters`] || [],
  };
};
